/**
 * Right-click context menu for report rows. Renders at mouse coordinates and
 * exposes the same action vocabulary as the row's kebab/icon column: View,
 * Edit, Delete, and any RowActionConfig entries the resource defines.
 *
 * Implementation note: rolling our own positioned menu rather than pulling in
 * `@radix-ui/react-context-menu` keeps the dependency footprint flat — every
 * other primitive in this codebase is already on Radix via shadcn, so a
 * direct DOM approach is leaner here.
 */
import React from 'react';
import { Eye, Pencil, Trash2 } from 'lucide-react';
import type { RowActionConfig } from './crud-page';

export interface RowContextMenuItem {
  label: string;
  icon?: React.ReactNode;
  destructive?: boolean;
  onSelect: () => void;
}

export interface RowContextMenuProps {
  x: number;
  y: number;
  items: RowContextMenuItem[];
  onClose: () => void;
}

/** Click-outside + Esc dismissal. */
function useDismiss(ref: React.RefObject<HTMLDivElement | null>, onClose: () => void) {
  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    // Fire on the next tick so the right-click that opened us doesn't
    // immediately dismiss it.
    const t = setTimeout(() => {
      document.addEventListener('mousedown', handleClick);
      document.addEventListener('keydown', handleKey);
    }, 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [ref, onClose]);
}

export function RowContextMenu({ x, y, items, onClose }: RowContextMenuProps) {
  const ref = React.useRef<HTMLDivElement>(null);
  useDismiss(ref, onClose);

  // Clamp into viewport so the menu doesn't render off-screen when the user
  // right-clicks near the right or bottom edge. We measure after mount.
  const [pos, setPos] = React.useState({ x, y });
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    setPos({
      x: Math.min(x, vw - w - 4),
      y: Math.min(y, vh - h - 4),
    });
  }, [x, y]);

  if (items.length === 0) return null;

  return (
    <div
      ref={ref}
      role="menu"
      style={{ position: 'fixed', top: pos.y, left: pos.x, zIndex: 100 }}
      className="min-w-[180px] rounded-md border bg-popover text-popover-foreground shadow-md py-1 text-sm"
    >
      {items.map((it, i) => (
        <button
          key={i}
          type="button"
          role="menuitem"
          onClick={() => { it.onSelect(); onClose(); }}
          className={
            'w-full text-left px-3 py-1.5 flex items-center gap-2 hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer ' +
            (it.destructive ? 'text-destructive' : '')
          }
        >
          {it.icon && <span className="w-4 h-4 flex-shrink-0">{it.icon}</span>}
          <span>{it.label}</span>
        </button>
      ))}
    </div>
  );
}

/** Convenience builder: stitch together the standard View / Edit / Delete /
 *  rowActions list with the right permission gates. Caller supplies the
 *  granular handlers. */
export function buildRowContextItems(opts: {
  row: any;
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  rowActions?: RowActionConfig[];
  onTriggerRowAction?: (a: RowActionConfig) => void;
}): RowContextMenuItem[] {
  const items: RowContextMenuItem[] = [];
  if (opts.onView) items.push({ label: 'View details', icon: <Eye className="h-4 w-4" />, onSelect: opts.onView });
  if (opts.onEdit) items.push({ label: 'Edit', icon: <Pencil className="h-4 w-4" />, onSelect: opts.onEdit });
  if (opts.rowActions && opts.onTriggerRowAction) {
    for (const a of opts.rowActions) {
      items.push({
        label: a.label,
        destructive: a.variant === 'destructive',
        onSelect: () => opts.onTriggerRowAction!(a),
      });
    }
  }
  if (opts.onDelete) items.push({ label: 'Delete', icon: <Trash2 className="h-4 w-4" />, destructive: true, onSelect: opts.onDelete });
  return items;
}
